import { useEffect, useRef } from 'react'
import { clsx } from 'clsx'
import type { Message, Therapist, MessageStatus } from '../../stores/chatStore'
import { useChatStore } from '../../stores/chatStore'
import VideoPlayer from './VideoPlayer'
import AudioPlayer from './AudioPlayer'
import ChatImage from './ChatImage'
import ReactionBadges from './ReactionBadges'
import TechDebugFooter from '../debug/TechDebugFooter'
import { useDevMode } from '../../hooks/useDevMode'
import { wsManager } from '../../api/websocket'

interface MessageBubbleProps {
  message: Message
  therapist?: Therapist | null
  isLast?: boolean
}

/**
 * Strip prompt formatting tags from message content.
 * Tags like [warmly], [pause], [softly] are for AI context, not display.
 */
function stripPromptTags(content: string): string {
  // Remove tags like [warmly], [pause], [softly], etc.
  let cleaned = content.replace(/\[[\w\s-]+\]/g, '')
  // Remove *action text* (character-driven statuses like *поднимает взгляд*)
  cleaned = cleaned.replace(/\*[^*]{3,80}\*/g, '')
  return cleaned.trim()
}

/**
 * Format duration in seconds to mm:ss display.
 */
function formatDuration(seconds: number): string {
  const mins = Math.floor(seconds / 60)
  const secs = Math.floor(seconds % 60)
  return `${mins}:${secs.toString().padStart(2, '0')}`
}

/**
 * Check if message is a video/voice-only message (no meaningful text to show).
 */
function isMediaOnlyMessage(content: string, hasVideo: boolean, hasVoice: boolean, hasImage: boolean): boolean {
  if (!hasVideo && !hasVoice && !hasImage) return false
  // Check if content is just media tags without real text (after cleaning)
  const cleaned = cleanMediaContent(content)
  return cleaned.length < 3
}

/**
 * Clean media message content for display:
 * - Remove [Видеосообщение], [Голосовое сообщение], [Фото] tags
 * - Remove "Текст:" prefix
 * - Remove emotion/recommendation metadata
 */
function cleanMediaContent(content: string): string {
  return content
    .replace(/\[Видеосообщение\]/gi, '')
    .replace(/\[Голосовое сообщение\]/gi, '')
    .replace(/\[Фото\]/gi, '')
    .replace(/\[СЕЛФИ\]/gi, '')
    .replace(/\[Анализ голоса:[^\]]*\]/gi, '')
    .replace(/Текст:\s*/gi, '')
    .replace(/\*\*Эмоция:\*\*.*/gi, '')
    .replace(/\*\*Мимика:\*\*.*/gi, '')
    .replace(/\*\*Рекомендация:\*\*.*/gi, '')
    // Plain text analysis (photo upload format)
    .replace(/Эмоция:\s*[^\n]+/gi, '')
    .replace(/Описание:\s*[^\n]+/gi, '')
    .replace(/Заметки:\s*[^\n]+/gi, '')
    .replace(/Рекомендуемый тон:\s*\S+/gi, '')
    .trim()
}

const therapistBubbleClasses: Record<string, string> = {
  wendy: 'bubble-wendy',
  artem: 'bubble-artem',
  finch: 'bubble-finch',
  una: 'bubble-una',
}

/**
 * Message status icon (Telegram-style)
 * - User messages: light color (on teal bg)
 * - Therapist messages: green color (on light bg)
 */
function MessageStatusIcon({ status, isUser = true }: { status?: MessageStatus; isUser?: boolean }) {
  if (!status) return null

  // User: light white/gray, Therapist: green
  const colorClass = isUser ? 'text-white/70' : 'text-green-500'

  switch (status) {
    case 'sending':
      // Animated clock
      return (
        <svg className={clsx('w-3.5 h-3.5 animate-pulse', colorClass)} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
      )
    case 'sent':
      // Single checkmark ✓
      return (
        <svg className={clsx('w-3.5 h-3.5', colorClass)} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M5 13l4 4L19 7" />
        </svg>
      )
    case 'seen':
      // Double checkmark ✓✓
      return (
        <svg className={clsx('w-4 h-3.5', colorClass)} viewBox="0 0 24 16" fill="none" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M1 8l4 4L13 4" />
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2.5} d="M7 8l4 4L19 4" />
        </svg>
      )
  }
}

// Avatar ring colors matching therapist accent
const avatarRingClasses: Record<string, string> = {
  wendy: 'ring-wendy',
  artem: 'ring-artem',
  finch: 'ring-finch',
  harold: 'ring-finch',
  una: 'ring-una',
}

export default function MessageBubble({ message, therapist, isLast = false }: MessageBubbleProps) {
  const { isDebugEnabled, isAdvancedDebug } = useDevMode()
  const isUser = message.role === 'user'
  const therapistType = therapist?.type || 'wendy'
  const hasVideo = !!message.videoUrl || !!(message.videoUrls && message.videoUrls.length > 0)
  const hasMultipleVideos = message.videoUrls && message.videoUrls.length > 1
  const hasVoice = !!message.voiceUrl
  const hasImage = !!message.imageUrl
  const isMediaOnly = isMediaOnlyMessage(message.content, hasVideo, hasVoice, hasImage)

  // Clean content for display
  const displayContent = (() => {
    let content = message.content
    // Remove media tags for user media messages
    if ((hasVideo || hasVoice || hasImage) && isUser) {
      content = cleanMediaContent(content)
    }
    // Strip prompt tags for assistant messages
    if (!isUser) {
      content = stripPromptTags(content)
    }
    // Always strip analysis metadata from message content
    // Debug info is shown via AgentInsightBubble (debugEvents), not raw tags in content
    content = content
      // Remove [VOICE_ANALYSIS]...[/VOICE_ANALYSIS] and [PHOTO_ANALYSIS]...[/PHOTO_ANALYSIS] blocks
      .replace(/\[VOICE_ANALYSIS\][\s\S]*?\[\/VOICE_ANALYSIS\]\s*/gi, '')
      .replace(/\[PHOTO_ANALYSIS\][\s\S]*?\[\/PHOTO_ANALYSIS\]\s*/gi, '')
      // Remove [Анализ голоса: ...] tags
      .replace(/\[Анализ голоса:[^\]]*\]/gi, '')
      // Remove [СЕЛФИ] tag
      .replace(/\[СЕЛФИ\]/gi, '')
      // Remove all [UPPERCASE_TAG: ...] patterns (hidden agent tags: GENDER, OBSERVER, HINT, SAY, etc.)
      .replace(/\[[A-ZА-ЯЁ_]+:[^\]]*\]/g, '')
      // Markdown bold format (video/voice)
      .replace(/\*\*Эмоция:\*\*.*/gi, '')
      .replace(/\*\*Мимика:\*\*.*/gi, '')
      .replace(/\*\*Рекомендация:\*\*.*/gi, '')
      .replace(/\*\*Анализ:\*\*.*/gi, '')
      // Plain text format (photo upload)
      .replace(/Эмоция:\s*[^\n]+/gi, '')
      .replace(/Описание:\s*[^\n]+/gi, '')
      .replace(/Заметки:\s*[^\n]+/gi, '')
      .replace(/Рекомендуемый тон:\s*\S+/gi, '')
      // Normalize excessive newlines (3+ → 2) left after tag stripping
      .replace(/\n{3,}/g, '\n\n')
      .trim()
    return content
  })()

  // Find quoted message for replies (targeted selector — only re-renders if quoted message changes)
  const quotedMessage = useChatStore((state) =>
    message.replyToMessageId
      ? state.messages.find((m) => m.id === message.replyToMessageId) ?? null
      : null
  )

  // Handle reaction click (toggle)
  const handleReactionClick = (emoji: string) => {
    wsManager.sendReaction(message.id, emoji)
  }

  // Intersection Observer for "seen" status on therapist messages
  const messageRef = useRef<HTMLDivElement>(null)
  const updateMessage = useChatStore((state) => state.updateMessage)

  useEffect(() => {
    // Only observe therapist messages that aren't already "seen"
    if (isUser || message.status === 'seen' || !messageRef.current) return

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && message.status !== 'seen') {
            // Mark as seen when scrolled into view
            updateMessage(message.id, { status: 'seen' })
            // Notify server (optional - for read receipts/analytics)
            wsManager.sendMessageSeen?.(message.id)
          }
        })
      },
      { threshold: 0.5 } // 50% visible
    )

    observer.observe(messageRef.current)
    return () => observer.disconnect()
  }, [isUser, message.id, message.status, updateMessage])

  return (
    <div
      ref={messageRef}
      className={clsx(
        'message-bubble flex gap-2',
        isUser ? 'justify-end' : 'justify-start items-end'
      )}
    >
      {/* Therapist avatar (only for bot messages) */}
      {!isUser && therapist?.avatarUrl && (
        <div
          className={clsx(
            'w-8 h-8 rounded-full overflow-hidden flex-shrink-0 ring-2',
            avatarRingClasses[therapistType] || 'ring-primary-500'
          )}
        >
          <img
            src={therapist.avatarUrl}
            alt={therapist.name || 'Терапевт'}
            className="w-full h-full object-cover"
            onError={(e) => {
              // Hide if image fails to load
              (e.target as HTMLImageElement).style.display = 'none'
            }}
          />
        </div>
      )}

      <div
        className={clsx(
          isUser
            ? 'bubble-user'
            : therapistBubbleClasses[therapistType] || 'bubble-assistant'
        )}
      >
        {/* Reply quote block */}
        {(quotedMessage || message.quotedText) && (
          <div
            className={clsx(
              'mb-2 pl-2 border-l-2 text-sm opacity-80',
              isUser ? 'border-white/50' : 'border-primary-400'
            )}
          >
            <div className={clsx('text-xs font-medium', isUser ? 'text-white/70' : 'text-primary-500')}>
              {quotedMessage?.role === 'user' ? 'Вы' : therapist?.name || 'Терапевт'}
            </div>
            <div className="line-clamp-2">
              {message.quotedText || (quotedMessage ? stripPromptTags(quotedMessage.content).slice(0, 100) : '')}
            </div>
          </div>
        )}

        {/* Text content - hide for media-only messages (video/voice without text) */}
        {!isMediaOnly && (
          <div className="leading-relaxed">
            <span className="whitespace-pre-wrap break-words leading-relaxed">
              {displayContent}
            </span>
          </div>
        )}

        {/* Emotion badge — only in debug mode (GREEN+BLUE) */}
        {isDebugEnabled && message.emotionDetected && message.emotionDetected !== 'neutral' && (
          <span className={clsx(
            'inline-block mt-2 px-2 py-0.5 text-xs rounded-full',
            isUser ? 'bg-white/20 text-white' : 'badge-primary'
          )}>
            {message.emotionDetected}
          </span>
        )}

        {/* Voice player - skip entirely if video exists (video already has audio) */}
        {message.voiceUrl && !hasVideo && (
          <div className="mt-3">
            <AudioPlayer
              src={message.voiceUrl}
              therapistType={isUser ? undefined : therapistType}
              autoPlay={isLast && !isUser}
              messageId={message.id}
              initialDuration={message.duration}
            />
          </div>
        )}

        {/* Video player (circular) - single or multi-circle */}
        {hasVideo && (
          <div className="mt-3 flex flex-col items-center gap-1">
            {/* Multi-circle: render all videos in a horizontal row */}
            {hasMultipleVideos ? (
              <div className="flex gap-2 flex-wrap justify-center">
                {message.videoUrls!.map((url, idx) => (
                  <div key={idx} className="flex-shrink-0 relative">
                    <VideoPlayer
                      src={url}
                      therapist={therapist}
                      isUser={isUser}
                      autoPlay={isLast && !isUser}
                      messageId={message.id + idx}  // Unique ID for queue
                    />
                    {/* Circle number indicator */}
                    <span className="absolute -top-1 -right-1 w-5 h-5 bg-dark-700 text-white text-xs rounded-full flex items-center justify-center">
                      {idx + 1}
                    </span>
                  </div>
                ))}
              </div>
            ) : (
              /* Single video - legacy behavior */
              <VideoPlayer
                src={message.videoUrl!}
                therapist={therapist}
                isUser={isUser}
                autoPlay={isLast && !isUser}
                messageId={message.id}
              />
            )}
            {/* Duration badge - use Number() to avoid JSX rendering "0" */}
            {Number(message.duration) > 0 && (
              <span className={clsx(
                'text-xs px-2 py-0.5 rounded-full',
                isUser ? 'bg-white/20 text-white' : 'bg-neutral-100 text-neutral-500'
              )}>
                {formatDuration(message.duration!)}
              </span>
            )}
          </div>
        )}

        {/* Image preview */}
        {message.imageUrl && (
          <div className="mt-3">
            <ChatImage src={message.imageUrl} />
          </div>
        )}

        {/* Timestamp + Edited + Status */}
        <div
          className={clsx(
            'flex items-center gap-1 text-xs mt-2',
            isUser ? 'text-primary-200 justify-end' : 'text-neutral-400'
          )}
        >
          {/* Status icon (before time for therapist, after for user) */}
          {!isUser && <MessageStatusIcon status={message.status} isUser={false} />}
          {/* Edited indicator */}
          {message.editedAt && (
            <span className="italic">ред.</span>
          )}
          <span>
            {message.createdAt && !isNaN(new Date(message.createdAt).getTime())
              ? new Date(message.createdAt).toLocaleTimeString('ru-RU', {
                  hour: '2-digit',
                  minute: '2-digit',
                })
              : ''
            }
          </span>
          {/* Status icon for user messages (after time) */}
          {isUser && <MessageStatusIcon status={message.status} />}
        </div>

        {/* Reactions */}
        {message.reactions && message.reactions.length > 0 && (
          <ReactionBadges
            reactions={message.reactions}
            isUser={isUser}
            onReactionClick={handleReactionClick}
          />
        )}

        {/* TechDebugFooter (BLUE mode only) — added in Phase 4 */}
        {isAdvancedDebug && message.debugInfo && (
          <TechDebugFooter debugInfo={message.debugInfo} />
        )}
      </div>
    </div>
  )
}
